import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { TerraDraw, TerraDrawRectangleMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { MODES } from './TerraDrawControls';

const RECTANGLE_STYLES = {
    fillColor: '#0069D2',
    fillOpacity: 0.3,
    outlineColor: '#0069D2',
    strokeColor: '#0069D2',
    strokeOpacity: 1,
    strokeWeight: 1,
    zIndex: 3,
};

const isTouchDevice = () => {
    return (
        typeof window !== 'undefined' &&
        ('ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0))
    );
};
const setTouchAction = (map, value) => {
    const el = map?.getDiv?.();
    if (!el) {
        return;
    }
    el.style.touchAction = value;
};

const setMapGestureHandling = (map, value) => {
    if (!map?.setOptions) {
        return;
    }
    map.setOptions({ gestureHandling: value });
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const getEffectiveMapMargin = (el) => {
    let node = el;
    while (node && node !== document.body) {
        const mapMargin = Number.parseFloat(
            window.getComputedStyle(node)?.marginRight || '0',
        ) || 0;
        if (mapMargin > 0) {
            return mapMargin;
        }
        node = node.parentElement;
    }
    return 0;
};

export default function TerraDrawManager({
    map,
    enabled,
    mode,
    onReady,
    onFinish,
    onModeChange,
}) {
    const drawRef = useRef(null);
    const previewOverlayRef = useRef(null);
    const isDraggingRef = useRef(false);
    const hasDraggedRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const mapMarginRef = useRef(0);

    const firstDrawSeenAtRef = useRef(0);
    const lastFinishAtRef = useRef(0);
    const finalizeRef = useRef(() => {});

    const onReadyRef = useRef(onReady);
    const onFinishRef = useRef(onFinish);
    const onModeChangeRef = useRef(onModeChange);

    useEffect(() => {
        onReadyRef.current = onReady;
        onFinishRef.current = onFinish;
        onModeChangeRef.current = onModeChange;
    });

    const clearPreview = () => {
        if (previewOverlayRef.current) {
            previewOverlayRef.current.setMap(null);
            previewOverlayRef.current = null;
        }
    };

    useEffect(() => {
        if (!map) {
            return;
        }
        if (!isTouchDevice()) {
            return;
        }

        if (enabled && mode === MODES.RECTANGLE) {
            setTouchAction(map, 'none');
            setMapGestureHandling(map, 'none');
        }
        else {
            setTouchAction(map, 'auto');
            setMapGestureHandling(map, 'greedy');
        }

        return () => {
            setTouchAction(map, 'auto');
            setMapGestureHandling(map, 'greedy');
        };
    }, [map, mode, enabled]);

    useEffect(() => {
        if (!map || !enabled || mode !== MODES.RECTANGLE) {
            return;
        }
        const mapDiv = map.getDiv?.();
        if (!mapDiv) {
            return;
        }

        const edgeSize = 40;
        const maxPan = 12;
        let rafId = null;

        const panBy = (dx, dy) => {
            if (!dx && !dy) {
                return;
            }
            map.panBy(dx, dy);
        };

        const onPointerDown = (event) => {
            isDraggingRef.current = true;
            hasDraggedRef.current = false;
            dragStartRef.current = { x: event.clientX, y: event.clientY };
            mapMarginRef.current = getEffectiveMapMargin(mapDiv);
        };

        const finalizeRectangle = () => {
            if (!drawRef.current) {
                return;
            }
            const sinceFinish = Date.now() - lastFinishAtRef.current;
            if (sinceFinish < 120) {
                return;
            }

            const snapshot = drawRef.current.getSnapshot();
            const polygons = snapshot.filter((f) => f?.geometry?.type === 'Polygon');
            const feature = polygons[polygons.length - 1];
            if (!feature) {
                clearPreview();
                firstDrawSeenAtRef.current = 0;
                drawRef.current.setMode(MODES.SELECT);
                onModeChangeRef.current?.(MODES.SELECT);
                return;
            }

            const idArr = feature.id ? [feature.id] : [];
            if (idArr.length > 0) {
                try {
                    drawRef.current.removeFeatures(idArr);
                }
                catch (error) {
                    console.warn(error);
                }
            }

            clearPreview();
            firstDrawSeenAtRef.current = 0;
            onFinishRef.current?.(drawRef.current, {
                ids: idArr,
                type: MODES.RECTANGLE,
                features: [feature],
                snapshot,
            });
            drawRef.current.setMode(MODES.SELECT);
            onModeChangeRef.current?.(MODES.SELECT);
        };
        finalizeRef.current = finalizeRectangle;

        const onPointerUp = () => {
            isDraggingRef.current = false;
            if (hasDraggedRef.current) {
                finalizeRectangle();
            }
            hasDraggedRef.current = false;
        };

        const onPointerMove = (event) => {
            if (!isDraggingRef.current || !firstDrawSeenAtRef.current) {
                return;
            }
            const dx = event.clientX - dragStartRef.current.x;
            const dy = event.clientY - dragStartRef.current.y;
            if (!hasDraggedRef.current && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
                hasDraggedRef.current = true;
            }
            const rect = mapDiv.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            let panX = 0;
            let panY = 0;

            if (x < edgeSize) {
                panX = -clamp(edgeSize - x, 0, maxPan);
            }
            else if (x > rect.width - edgeSize - mapMarginRef.current) {
                panX = clamp(x - (rect.width - edgeSize - mapMarginRef.current), 0, maxPan);
            }

            if (y < edgeSize) {
                panY = -clamp(edgeSize - y, 0, maxPan);
            }
            else if (y > rect.height - edgeSize) {
                panY = clamp(y - (rect.height - edgeSize), 0, maxPan);
            }

            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => panBy(panX, panY));
        };

        mapDiv.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchend', onPointerUp);
        window.addEventListener('touchcancel', onPointerUp);
        mapDiv.addEventListener('pointermove', onPointerMove);

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            mapDiv.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            window.removeEventListener('mouseup', onPointerUp);
            window.removeEventListener('touchend', onPointerUp);
            window.removeEventListener('touchcancel', onPointerUp);
            mapDiv.removeEventListener('pointermove', onPointerMove);
        };
    }, [map, enabled, mode]);

    useEffect(() => {
        if (!map) {
            return;
        }
        if (!window?.google?.maps) {
            return;
        }

        if (!enabled) {
            drawRef.current?.stop?.();
            drawRef.current = null;
            clearPreview();
            firstDrawSeenAtRef.current = 0;
            onReadyRef.current?.(null);
            return;
        }

        const draw = new TerraDraw({
            adapter: new TerraDrawGoogleMapsAdapter({
                map,
                lib: window.google.maps,
                coordinatePrecision: 9,
            }),
            modes: [
                new TerraDrawSelectMode({}),
                new TerraDrawRectangleMode({
                    drawInteraction: 'click-move-or-drag',
                    styles: {
                        fillColor: RECTANGLE_STYLES.fillColor,
                        fillOpacity: RECTANGLE_STYLES.fillOpacity,
                        outlineColor: RECTANGLE_STYLES.outlineColor,
                        outlineWidth: RECTANGLE_STYLES.strokeWeight,
                    },
                }),
            ],
        });

        drawRef.current = draw;
        draw.__finalizeRectangle = () => finalizeRef.current?.();

        const handleReady = () => {
            onReadyRef.current?.(draw);
            draw.setMode(MODES.SELECT);
            onModeChangeRef.current?.(MODES.SELECT);
        };

        const handleChange = () => {
            const snapshot = draw.getSnapshot();
            let drawingFeature = null;
            for (let i = snapshot.length - 1; i >= 0; i -= 1) {
                const f = snapshot[i];
                if (f?.geometry?.type === 'Polygon') {
                    drawingFeature = f;
                    break;
                }
            }

            if (!drawingFeature) {
                return;
            }

            if (!firstDrawSeenAtRef.current) {
                firstDrawSeenAtRef.current = Date.now();
            }

            const coordinates = drawingFeature.geometry.coordinates?.[0];
            if (!Array.isArray(coordinates) || coordinates.length === 0) {
                return;
            }

            const paths = coordinates.map(([lng, lat]) => ({ lat, lng }));

            if (previewOverlayRef.current) {
                previewOverlayRef.current.setPaths(paths);
            }
            else {
                previewOverlayRef.current = new window.google.maps.Polygon({
                    paths,
                    ...RECTANGLE_STYLES,
                    clickable: false,
                    draggable: false,
                    map,
                });
            }
        };

        const handleFinish = (ids, type) => {
            lastFinishAtRef.current = Date.now();
            const snapshot = draw.getSnapshot();
            const idArr = Array.isArray(ids) ? ids : ids ? [ids] : [];
            const features = idArr.length ? snapshot.filter((f) => idArr.includes(f.id)) : snapshot;

            const startedAt = firstDrawSeenAtRef.current;
            firstDrawSeenAtRef.current = 0;
            const elapsed = startedAt ? Date.now() - startedAt : 9999;

            if (elapsed < 120) {
                if (idArr.length > 0) {
                    try {
                        draw.removeFeatures(idArr);
                    }
                    catch (error) {
                        console.warn(error);
                    }
                }
                clearPreview();
                draw.setMode(MODES.SELECT);
                onModeChangeRef.current?.(MODES.SELECT);
                return;
            }

            if (idArr.length > 0) {
                try {
                    draw.removeFeatures(idArr);
                }
                catch (error) {
                    console.warn(error);
                }
            }

            clearPreview();

            onFinishRef.current?.(draw, { ids: idArr, type, features, snapshot });

            draw.setMode(MODES.SELECT);
            onModeChangeRef.current?.(MODES.SELECT);
        };

        draw.on('ready', handleReady);
        draw.on('change', handleChange);
        draw.on('finish', handleFinish);

        let started = false;
        const tryStart = () => {
            if (started) {
                return true;
            }
            if (!enabled) {
                return false;
            }
            const hasDiv = !!map?.getDiv?.();
            const hasBounds = !!map?.getBounds?.();
            if (!hasDiv || !hasBounds) {
                return false;
            }

            started = true;
            draw.start();
            return true;
        };

        const idleListener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
            if (tryStart()) {
                clearInterval(pollTimer);
            }
        });

        const pollTimer = setInterval(() => {
            if (tryStart()) {
                clearInterval(pollTimer);
                window.google.maps.event.removeListener(idleListener);
            }
        }, 50);

        return () => {
            clearInterval(pollTimer);
            window.google.maps.event.removeListener(idleListener);
            try {
                draw.off('ready', handleReady);
                draw.off('change', handleChange);
                draw.off('finish', handleFinish);
            }
            catch (error) {
                console.warn(error);
            }
            draw.stop?.();
            drawRef.current = null;
            clearPreview();
            firstDrawSeenAtRef.current = 0;
            onReadyRef.current?.(null);
        };
    }, [map, enabled]);

    useEffect(() => {
        if (!enabled || mode !== MODES.SELECT) {
            return;
        }
        if (!firstDrawSeenAtRef.current) {
            return;
        }
        finalizeRef.current?.();
    }, [enabled, mode]);

    return null;
}

TerraDrawManager.propTypes = {
    map: PropTypes.shape({
        getDiv: PropTypes.func,
        getBounds: PropTypes.func,
        panBy: PropTypes.func,
    }).isRequired,
    enabled: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf([MODES.SELECT, MODES.RECTANGLE]),
    onReady: PropTypes.func,
    onFinish: PropTypes.func,
    onModeChange: PropTypes.func,
};

TerraDrawManager.defaultProps = {
    mode: MODES.SELECT,
    onReady: undefined,
    onFinish: undefined,
    onModeChange: undefined,
};
